import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, ChevronRight } from 'lucide-react';
import { companyInfo, services, cities } from '../../data/mock';

const Footer = () => {
  const primaryCities = cities.filter(c => c.isPrimary);
  const otherCities = cities.filter(c => !c.isPrimary).slice(0, 6);

  return (
    <footer className="bg-[hsl(215,25%,12%)] text-white">
      {/* Main footer content */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Company Info */}
          <div>
            <img 
              src={companyInfo.logo} 
              alt="NGL SERVICES" 
              className="h-16 w-auto mb-6 brightness-110"
            />
            <p className="text-gray-400 mb-6 text-sm leading-relaxed">
              Spécialiste en menuiserie extérieure, vitrerie, serrurerie et dépannage d'urgence. 
              Intervention rapide dans tout le Val-de-Marne.
            </p>
            <div className="flex flex-col gap-3">
              <a 
                href={`tel:${companyInfo.phone}`}
                className="flex items-center gap-3 text-gray-300 hover:text-[hsl(24,95%,53%)] transition-colors"
              >
                <Phone className="w-5 h-5 text-[hsl(207,78%,50%)]" />
                <span>{companyInfo.phone}</span>
              </a>
              <a 
                href={`tel:${companyInfo.phoneEmergency}`}
                className="flex items-center gap-3 text-[hsl(24,95%,53%)] font-semibold"
              >
                <Phone className="w-5 h-5 animate-pulse" />
                <span>Urgences : {companyInfo.phoneEmergency}</span>
              </a>
              <a 
                href={`mailto:${companyInfo.email}`}
                className="flex items-center gap-3 text-gray-300 hover:text-[hsl(24,95%,53%)] transition-colors"
              >
                <Mail className="w-5 h-5 text-[hsl(207,78%,50%)]" />
                <span>{companyInfo.email}</span>
              </a>
              <div className="flex items-center gap-3 text-gray-300">
                <MapPin className="w-5 h-5 text-[hsl(207,78%,50%)]" />
                <span>{companyInfo.address}</span>
              </div>
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-white">Nos Services</h3>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service.id}>
                  <Link 
                    to={`/services/${service.id}`}
                    className="footer-link flex items-center gap-2 text-gray-400 hover:text-[hsl(24,95%,53%)] text-sm"
                  >
                    <ChevronRight className="w-4 h-4" />
                    {service.title}
                  </Link>
                </li>
              ))}
              {/* Lien SEO rénovation fenêtres */}
              <li>
                <Link 
                  to="/renovation-fenetres-paiement-plusieurs-fois-villiers-sur-marne"
                  className="footer-link flex items-center gap-2 text-[hsl(24,95%,53%)] hover:text-[hsl(24,95%,60%)] text-sm font-medium"
                >
                  <ChevronRight className="w-4 h-4" />
                  Rénovation fenêtres (paiement facilité)
                </Link>
              </li>
            </ul>
          </div>

          {/* Zones d'intervention */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-white">Zones d'intervention</h3>
            <ul className="space-y-3">
              {primaryCities.map((city) => (
                <li key={city.id}>
                  <Link 
                    to={`/villes/${city.id}`}
                    className="footer-link flex items-center gap-2 text-[hsl(24,95%,53%)] font-medium text-sm"
                  >
                    <ChevronRight className="w-4 h-4" />
                    {city.name} (Siège)
                  </Link>
                </li>
              ))}
              {otherCities.map((city) => (
                <li key={city.id}>
                  <Link 
                    to={`/villes/${city.id}`}
                    className="footer-link flex items-center gap-2 text-gray-400 hover:text-[hsl(24,95%,53%)] text-sm"
                  >
                    <ChevronRight className="w-4 h-4" />
                    {city.name}
                  </Link>
                </li>
              ))}
              <li>
                <Link 
                  to="/villes/villiers-sur-marne"
                  className="text-[hsl(207,78%,50%)] text-sm hover:underline"
                >
                  Voir toutes les villes →
                </Link>
              </li>
            </ul>
          </div>

          {/* Horaires & Urgences */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-white">Horaires & Urgences</h3>
            <div className="bg-[hsl(215,25%,18%)] rounded-xl p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[hsl(24,95%,53%)] flex items-center justify-center">
                  <Clock className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-white">Urgences</p>
                  <p className="text-[hsl(24,95%,53%)] font-bold">24h/24 – 7j/7</p>
                </div>
              </div>
              <p className="text-gray-400 text-sm mb-4">
                En cas d'urgence (porte claquée, serrure cassée, vitre brisée...), 
                contactez-nous immédiatement.
              </p>
              <a 
                href={`tel:${companyInfo.phoneEmergency}`}
                className="block w-full py-3 px-4 bg-[hsl(24,95%,53%)] text-white text-center font-semibold rounded-lg hover:bg-[hsl(24,95%,45%)] transition-colors"
              >
                <Phone className="w-4 h-4 inline mr-2" />
                Appeler maintenant
              </a>
            </div>

            <div className="mt-6">
              <h4 className="font-medium text-white mb-3">Liens utiles</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/a-propos" className="footer-link text-gray-400 hover:text-[hsl(24,95%,53%)] text-sm">
                    À propos de NGL SERVICES
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="footer-link text-gray-400 hover:text-[hsl(24,95%,53%)] text-sm">
                    Contact & Devis
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-gray-800">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm text-center md:text-left">
              © {new Date().getFullYear()} NGL SERVICES – Tous droits réservés. 
              Menuisier à Villiers-sur-Marne (94)
            </p>
            <div className="flex items-center gap-6 text-sm">
              <Link to="/mentions-legales" className="text-gray-500 hover:text-gray-300 transition-colors">
                Mentions légales
              </Link>
              <Link to="/politique-confidentialite" className="text-gray-500 hover:text-gray-300 transition-colors">
                Politique de confidentialité
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
